import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {derivedThemeAtom} from 'atoms/layout';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';

import {getStatusStyle, isOpenStatus, StatusButtonIcon} from './StatusButtonIcon';

/*
 * StatusButton is a button that displays a status with an icon
 *
 * @param {string} status - The status to display
 * @param {string} substatus - The optional substatus to display
 * @param {string} asStatus - The status to display as (if different from the status)
 * @param {boolean} isLoading - Whether the button is in a loading state
 * @param {boolean} isDisabled - Whether the button is disabled (also removes the border)
 * @param {boolean} isLabel - Whether the button should be displayed as a label (removes the padding)
 * @param {() => void} onClick - The function to call when the button is clicked
 */
export const StatusChip = ({
  status = 'no_status',
  substatus,
  asStatus,
  size = 'xsmall',
  includeArchived = false,
  statusColor,
  label,
}: {
  status?: string;
  substatus?: string;
  asStatus?: string;
  icon?: React.ReactNode;
  size?: 'xsmall' | 'small';
  includeArchived?: boolean;
  statusColor?: string;
  // Replaces the status name (e.g. "Archived Oct 15").
  label?: string;
}) => {
  const isDark = useAtomValue(derivedThemeAtom) !== 'bright';
  const effectiveStatus = asStatus ?? status;
  const defaultStatusColor = getStatusStyle(effectiveStatus).color;
  const hasTransparentBg =
    effectiveStatus === 'no_status' ||
    effectiveStatus === 'not_started' ||
    isOpenStatus(effectiveStatus);
  const translucentBackground = statusColor
    ? `color-mix(in srgb, ${statusColor} ${isDark ? 31 : 13}%, transparent)`
    : defaultStatusColor + (isDark ? '50' : '20');

  return (
    <Box
      borderRadius="XLarge"
      display="flex"
      alignItems="center"
      paddingLeft="6"
      paddingRight="8"
      paddingY={size === 'small' ? '4' : '2'}
      width="fit-content"
      style={{
        gap: 2,
        backgroundColor: hasTransparentBg ? 'transparent' : translucentBackground,
        border: hasTransparentBg ? '1px solid var(--dig-color__border__subtle)' : undefined,
      }}
    >
      <StatusButtonIcon
        status={effectiveStatus}
        substatus={substatus}
        size={size}
        includeArchived={includeArchived}
        statusColor={statusColor}
      />

      <Text size={size} variant="label" isBold>
        {label ?? t(status).toString()}
      </Text>
    </Box>
  );
};
